'use client';

import { useState } from 'react';
import { Zap, Infinity, Clock, RefreshCw, Shield, X } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useMondayAuth } from '@/contexts/MondayAuthContext';
import { toastUtils } from '@/lib/toast';

interface UpgradeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  featureType: 'recurring_pattern' | 'bulk_operation';
}

const BENEFITS = [
  { icon: Infinity, label: 'Unlimited operations', description: 'No monthly caps on bulk ops or items' },
  { icon: RefreshCw, label: 'Unlimited recurring tasks', description: 'No caps on executions or active patterns' },
  { icon: Clock, label: '48-hour rollback window', description: 'Double the safety net for changes' },
  { icon: Shield, label: 'No item limits', description: 'Process unlimited items per operation' },
];

const SOCIAL_PROOF: Record<string, string> = {
  recurring_pattern: 'Teams using recurring tasks save an average of 4 hours per week.',
  bulk_operation: 'Pro users process 10x more items with zero manual effort.',
};

export function UpgradeModal({ open, onOpenChange, featureType }: UpgradeModalProps) {
  const { monday, refreshAuth } = useMondayAuth();
  const [isPurchasing, setIsPurchasing] = useState(false);

  const featureLabel = featureType === 'recurring_pattern' ? 'recurring tasks' : 'bulk operations';

  const handlePurchase = async () => {
    setIsPurchasing(true);
    try {
      await monday.execute('purchase', { planId: 'professional_monthly' });
      await refreshAuth();
      onOpenChange(false);
    } catch (error) {
      console.error('Purchase failed:', error);
      toastUtils.error(
        'Purchase not available',
        'In-app purchases are not yet configured. Please check back soon.'
      );
    } finally {
      setIsPurchasing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="md" showClose={false}>
        {/* Blue header */}
        <div className="bg-primary-600 rounded-t-lg px-6 py-5 text-white relative">
          <button
            onClick={() => onOpenChange(false)}
            className="absolute right-4 top-4 rounded-sm text-white/70 hover:text-white transition-opacity focus:outline-none focus:ring-2 focus:ring-white/50"
            aria-label="Close dialog"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 mb-1">
            <Zap className="w-5 h-5" />
            <h2 className="text-lg font-semibold">Upgrade to Pro</h2>
          </div>
          <p className="text-primary-200 text-sm">
            Unlock unlimited {featureLabel}
          </p>
        </div>

        {/* Benefits */}
        <div className="px-6 py-5 space-y-4">
          {BENEFITS.map(({ icon: Icon, label, description }) => (
            <div key={label} className="flex items-start gap-3">
              <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary-50 flex items-center justify-center">
                <Icon className="w-4 h-4 text-primary-600" />
              </div>
              <div>
                <div className="text-sm font-medium text-[var(--text-heading)]">{label}</div>
                <div className="text-xs text-[var(--text-secondary)]">{description}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Pricing card */}
        <div className="mx-6 mb-4 rounded-lg bg-[var(--surface-bg)] border border-[var(--border-default)] px-4 py-3">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-sm font-semibold text-[var(--text-heading)]">Pro Plan</span>
              <span className="text-xs text-[var(--text-secondary)] ml-2">Flat rate &middot; unlimited users &middot; cancel anytime</span>
            </div>
            <span className="text-lg font-bold text-[var(--text-heading)]">$29<span className="text-xs font-normal text-[var(--text-secondary)]">/mo</span></span>
          </div>
        </div>

        {/* CTA button */}
        <div className="px-6 pb-4">
          <button
            onClick={handlePurchase}
            disabled={isPurchasing}
            className="w-full bg-primary-600 text-white py-3 px-4 rounded-lg text-sm font-semibold hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isPurchasing ? 'Processing...' : 'Upgrade to Pro - $29/month'}
          </button>
        </div>

        {/* Social proof */}
        <div className="px-6 pb-5">
          <p className="text-xs text-center text-[var(--text-muted)]">
            {SOCIAL_PROOF[featureType]}
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
